import { GeoCoordinatesEnum } from 'sigma-mappings';

const DEFAULT_DECIMALS = 2;

/**
 * Mascará de CPF.
 * Pattern suportado pelo v-mask.
 */
export const cpfMask = '###.###.###-##';

/**
 * Mascará de CNPJ.
 * Pattern suportado pelo v-mask.
 */
export const cnpjMask = '##.###.###/####-##';

/**
 * Mascará de telefone no padrão brasileiro.
 * Pattern suportado pelo v-mask.
 */
export const brazilianPhoneMask = ['(##) ####-####', '(##) #####-####'];

/**
 * Opções padrão para mascará de campos numéricos.
 * Opções suportadas pelo v-money.
 * O conteúdo do objeto deve ser traduzido. (Ver ObjectContentTranslator)
 */
export const numericOptions = {
  decimal: '$i18n$decimal-number.decimal-separator',
  thousands: '$i18n$decimal-number.thousand-separator',
  prefix: '',
  suffix: '',
  precision: DEFAULT_DECIMALS,
  masked: true,
  /**
   * Customizar opções, estendendo as opções padrões com novas opções informadas.
   * @param {Object} newOptions
   */
  customize(newOptions) {
    return Object.assign(Object.assign({}, this), newOptions);
  },
  /**
   * Constrói opções numéricas com base em definições de campos.
   * @param {import('@/entities/fieldDefinitions.js').FieldDefinitions} fieldsDefinitions
   * Definições de campos.
   * @returns {Object} Opções numéricas para cada campo. Segue a mesma estrutura dos dados e cada
   * campo numérico terá seu objeto de opções numéricas.
   */
  customizeForFieldsDefinitions(fieldsDefinitions) {
    return Object.keys(fieldsDefinitions).reduce((customizedOptions, fieldName) => {
      const fieldDefinition = fieldsDefinitions[fieldName];
      let options = null;

      if (fieldDefinition.type === Object) {
        options = this.customizeForFieldsDefinitions(fieldDefinition);
      }

      if (fieldDefinition.type === Number) {
        options = this.customize({
          precision: fieldDefinition.precision || 0,
        });
      }

      if (options != null) {
        // eslint-disable-next-line no-param-reassign
        customizedOptions[fieldName] = options;
      }

      return customizedOptions;
    }, {});
  },
};

export const clearFloatMask = (value, precision = DEFAULT_DECIMALS) => {
  const numbersArray = (value || '').match(/[0-9]+/g);
  const isNegative = (value || '').startsWith('-');
  const numbersString = (numbersArray || []).join('');
  const decimalSeparatorIndex = numbersString.length - precision;
  const integerPart = numbersString.substring(0, decimalSeparatorIndex);
  const floatPart = numbersString.substring(decimalSeparatorIndex);
  const stringFormattedValue = `${integerPart}.${floatPart}`;
  return parseFloat(`${isNegative ? '-' : ''}${stringFormattedValue}`);
};

const getGeoCoordinatesMaskParams = (geoCoordinatesType) => {
  if (geoCoordinatesType === GeoCoordinatesEnum.LATITUDE) {
    return {
      positiveIntegerLength: 2,
      negativeIntegerLength: 3,
      positivePreffix: '-PP',
      negativePreffix: '-PPP',
      suffix: '###############',
    };
  }

  return {
    positiveIntegerLength: 3,
    negativeIntegerLength: 4,
    positivePreffix: '-PPP',
    negativePreffix: '-PPPP',
    suffix: '###############',
  };
};

export const geoCoordinatesPattern = () => ({
  '-': {
    pattern: /^[-?\d]/,
  },
  P: {
    pattern: /^[.?\d]/,
  },
  '#': { pattern: /\d/ },
});

export const geoCoordinatesMask = (term, geoCoordinatesType) => {
  const TERM_LENGTH = term ? term.length : 0;
  const POSITION_TRACE = String(term).indexOf('-');
  const HAS_TRACE = POSITION_TRACE !== -1;
  const POSITION_POINT = String(term).indexOf('.');
  const HAS_POINT = POSITION_POINT !== -1;

  const PARAMS = getGeoCoordinatesMaskParams(geoCoordinatesType);

  const mask = HAS_TRACE
    ? PARAMS.negativePreffix
    : PARAMS.positivePreffix;

  const splitedMask = [...mask];
  let { suffix } = PARAMS;

  if (!term || !TERM_LENGTH) {
    return `${PARAMS.negativePreffix}${suffix}`;
  }

  if (HAS_POINT) {
    splitedMask.forEach((letter, indice) => {
      if (indice !== 0 && indice < POSITION_POINT) {
        splitedMask[indice] = 'P';
      }

      if (indice === POSITION_POINT) {
        splitedMask[indice] = 'P';
      }

      if (indice !== 0 && indice > POSITION_POINT) {
        splitedMask[indice] = '#';
        suffix = suffix.slice(0, suffix.length - 1);
      }
    });
  }

  // Add point automatically
  if (!HAS_TRACE && !HAS_POINT && TERM_LENGTH >= PARAMS.positiveIntegerLength) {
    splitedMask[PARAMS.positiveIntegerLength] = '.';
  }

  if (HAS_TRACE && !HAS_POINT && TERM_LENGTH >= PARAMS.negativeIntegerLength) {
    splitedMask[PARAMS.negativeIntegerLength] = '.';
  }

  return splitedMask.join('')
    .concat(suffix);
};
